/**
 * Pagayo Storefront - Main Entry Point
 *
 * This is the new Vite-powered frontend.
 * Each page has its own entry point for code-splitting.
 *
 * PERFORMANCE:
 * - Header + Footer → dynamic import, webshop pages only (FCP)
 * - Cart, Auth → deferred dynamic import, webshop only
 * - Page-specific code → dynamic import per pageType
 * - Admin/Platform pages load ONLY their specific SPA bundle (zero webshop modules)
 * - Analytics/Web Vitals → lazy loaded, non-blocking
 * - Target: <100KB initial load (gzipped)
 */

// Preact (required for mount)
import { hydrate, render } from 'preact';
import type { ComponentChild } from 'preact';
import { getPageContext } from './utils/page-context';
import { getPublicSettings, peekSsrPublicSettings } from './utils/public-settings';
import {
  applyStorefrontAppearanceToDocument,
  readStorefrontAppearanceFromPublicSettings,
} from './utils/storefront-appearance';
import { registerPwaServiceWorker } from './lib/register-pwa';
import { patchStorefrontSsrTranslationLeaks } from './lib/fix-ssr-translation-leaks';
import { hasStorefrontFooterTypographyOverrides } from './features/layout/storefront-typography';
import { resolveHeaderMenuSticky } from '../lib/layout/header-menu-sticky';
import { resolveTopBarSocialIconColor } from '../lib/layout/top-bar-social-colors';
import { isMembersDeskTerminalPageType } from '../lib/members-balie-path';

/** Webshop-only; dynamic import keeps i18n (~1MB locales) off admin/POS main path. */
const renderWithI18n = (vnode: ComponentChild, mount: HTMLElement): void => {
  void import('./i18n/webshop')
    .then(({ WebshopI18nProvider, detectClientLocale }) => {
      render(
        <WebshopI18nProvider initialLocale={detectClientLocale()}>{vnode}</WebshopI18nProvider>,
        mount,
      );
    })
    .catch((err: unknown) => {
      console.warn('[pagayo] Failed to load webshop i18n:', err);
    });
};

const hydrateWithI18n = (vnode: ComponentChild, mount: HTMLElement): void => {
  void import('./i18n/webshop')
    .then(({ WebshopI18nProvider, detectClientLocale }) => {
      hydrate(
        <WebshopI18nProvider initialLocale={detectClientLocale()}>{vnode}</WebshopI18nProvider>,
        mount,
      );
    })
    .catch((err: unknown) => {
      console.warn('[pagayo] Failed to hydrate webshop i18n:', err);
    });
};

/** Whether the navigator has been initialized (prevent double init) */
let navigatorInitialized = false;

const mountWebsiteEditorIfBootstrapped = async (): Promise<void> => {
  const { readWebsiteEditorShellBootstrap } = await import(
    "./features/website-editor/website-edit-shared"
  );
  if (!readWebsiteEditorShellBootstrap()) {
    return;
  }
  const { mountWebsiteEditorExperience } = await import(
    "./features/website-editor/WebsiteEditorExperience"
  );
  mountWebsiteEditorExperience();
};

const initWebshopNavigator = (): void => {
  if (navigatorInitialized) return;
  navigatorInitialized = true;
  void import('./lib/navigator').then(({ initNavigator }) => {
    initNavigator();
  }).catch((err: unknown) => {
    console.warn('[pagayo] Navigator init failed (MPA fallback active):', err);
  });
};

const initStorefrontAppearance = (isWebshop: boolean): void => {
  if (!isWebshop) {
    return;
  }
  const ssrAppearance = readStorefrontAppearanceFromPublicSettings(peekSsrPublicSettings());
  const tenantAppearance = (window as Window & {
    __TENANT__?: { storefrontAppearanceSettings?: unknown };
  }).__TENANT__?.storefrontAppearanceSettings;
  applyStorefrontAppearanceToDocument(ssrAppearance ?? tenantAppearance);
  void getPublicSettings()
    .then((settings) => {
      applyStorefrontAppearanceToDocument(
        readStorefrontAppearanceFromPublicSettings(settings),
      );
    })
    .catch((err: unknown) => {
      console.warn('[pagayo] Failed to apply storefront appearance settings:', err);
    });
};

/**
 * Remove accidental admin chrome artifacts from webshop pages.
 *
 * In some navigation edge-cases (content-swap + mixed cached shells),
 * an admin navbar fragment can remain in the DOM. That fragment is
 * recognizable by `.main-nav__autosave` and should never be visible
 * on storefront pages.
 */
const cleanupStrayAdminChrome = (): void => {
  const strayAdminNav = document.querySelector('.main-nav .main-nav__autosave')?.closest('.main-nav');
  if (strayAdminNav) {
    strayAdminNav.remove();
  }

  const strayAdminSidebar = document.querySelector('.admin-account-nav');
  if (strayAdminSidebar) {
    strayAdminSidebar.remove();
  }
};

/**
 * Mount critical layout components (Header + Footer).
 * Only loaded on webshop pages via dynamic import — avoids unnecessary
 * modulepreload warnings on admin/platform/social pages.
 * Uses Promise.all for parallel loading — renders as soon as both resolve.
 * After mount, initializes the content-swap navigator for persistent shell.
 *
 * @param isWebshop - Whether current page is a webshop context
 */
const mountCriticalComponents = (isWebshop: boolean): void => {
  if (!isWebshop) return;

  patchStorefrontSsrTranslationLeaks();
  cleanupStrayAdminChrome();

  const tenantConfig = (window as Window & {
    __TENANT__?: {
      name?: string;
      logoUrl?: string | null;
      showHeaderSearch?: boolean | null;
      showHeaderAccount?: boolean | null;
      showHeaderCart?: boolean | null;
      headerMenuSticky?: boolean | null;
      headerNavBarBackgroundColor?: string | null;
      headerSubmenuBackgroundColor?: string | null;
      headerMenuHoverBackgroundColor?: string | null;
      headerSubmenuHoverBackgroundColor?: string | null;
      headerChromeSticky?: boolean | null;
      headerChromeBackgroundColor?: string | null;
      headerChromeHoverBackgroundColor?: string | null;
      headerSearchPanelBackgroundColor?: string | null;
      headerSearchPanelHoverBackgroundColor?: string | null;
      headerAccountPanelBackgroundColor?: string | null;
      headerAccountPanelHoverBackgroundColor?: string | null;
      headerCartDrawerBackgroundColor?: string | null;
      headerCartDrawerHoverBackgroundColor?: string | null;
      showFooterNewsletter?: boolean | null;
      showTopBar?: boolean | null;
      topBarShowPhone?: boolean | null;
      topBarShowEmail?: boolean | null;
      topBarShowFacebook?: boolean | null;
      topBarShowX?: boolean | null;
      topBarShowWhatsApp?: boolean | null;
      topBarPhone?: string | null;
      topBarEmail?: string | null;
      socialFacebook?: string | null;
      socialTwitter?: string | null;
      socialWhatsApp?: string | null;
      topBarBackgroundColor?: string | null;
      topBarSocialFacebookColor?: string | null;
      topBarSocialXColor?: string | null;
      topBarSocialWhatsAppColor?: string | null;
      headerFooterTypography?: unknown;
    };
  }).__TENANT__;
  const ssrPublicSettings = peekSsrPublicSettings();
  const headerFooterTypography =
    ssrPublicSettings?.headerFooterTypography ?? tenantConfig?.headerFooterTypography;
  const forceClientFooterRender = hasStorefrontFooterTypographyOverrides(
    headerFooterTypography,
  );

  Promise.all([
    import('./features/layout/Header'),
    import('./features/layout/Footer'),
    import('./features/layout/enhanceSsrHeader'),
  ]).then(([{ Header }, { Footer }, { enhanceSsrHeader }]) => {
    const headerMount = document.getElementById('header-mount');
    if (headerMount) {
      try {
        const shopName = headerMount.dataset.shopName || tenantConfig?.name || 'Webshop';
        const logoUrl = headerMount.dataset.logoUrl || tenantConfig?.logoUrl || undefined;
        const showSearch = tenantConfig?.showHeaderSearch !== false;
        const showAccount = tenantConfig?.showHeaderAccount !== false;
        const showCart = tenantConfig?.showHeaderCart !== false;
        const headerOptions = {
          shopName,
          logoUrl,
          showSearch,
          showAccount,
          showCart,
          headerMenuSticky: resolveHeaderMenuSticky(
            tenantConfig?.headerMenuSticky ?? peekSsrPublicSettings()?.headerMenuSticky,
          ),
          headerNavBarBackgroundColor:
            tenantConfig?.headerNavBarBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerNavBarBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerNavBarBackgroundColor
              : ''),
          headerSubmenuBackgroundColor:
            tenantConfig?.headerSubmenuBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerSubmenuBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerSubmenuBackgroundColor
              : ''),
          headerMenuHoverBackgroundColor:
            tenantConfig?.headerMenuHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerMenuHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerMenuHoverBackgroundColor
              : ''),
          headerSubmenuHoverBackgroundColor:
            tenantConfig?.headerSubmenuHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerSubmenuHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerSubmenuHoverBackgroundColor
              : ''),
          headerChromeSticky:
            tenantConfig?.headerChromeSticky === true
            || peekSsrPublicSettings()?.headerChromeSticky === true,
          headerChromeBackgroundColor:
            tenantConfig?.headerChromeBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerChromeBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerChromeBackgroundColor
              : ''),
          headerChromeHoverBackgroundColor:
            tenantConfig?.headerChromeHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerChromeHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerChromeHoverBackgroundColor
              : ''),
          headerSearchPanelBackgroundColor:
            tenantConfig?.headerSearchPanelBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerSearchPanelBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerSearchPanelBackgroundColor
              : ''),
          headerSearchPanelHoverBackgroundColor:
            tenantConfig?.headerSearchPanelHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerSearchPanelHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerSearchPanelHoverBackgroundColor
              : ''),
          headerAccountPanelBackgroundColor:
            tenantConfig?.headerAccountPanelBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerAccountPanelBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerAccountPanelBackgroundColor
              : ''),
          headerAccountPanelHoverBackgroundColor:
            tenantConfig?.headerAccountPanelHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerAccountPanelHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerAccountPanelHoverBackgroundColor
              : ''),
          headerCartDrawerBackgroundColor:
            tenantConfig?.headerCartDrawerBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerCartDrawerBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerCartDrawerBackgroundColor
              : ''),
          headerCartDrawerHoverBackgroundColor:
            tenantConfig?.headerCartDrawerHoverBackgroundColor
            ?? (typeof peekSsrPublicSettings()?.headerCartDrawerHoverBackgroundColor === 'string'
              ? peekSsrPublicSettings()?.headerCartDrawerHoverBackgroundColor
              : ''),
          showTopBar: tenantConfig?.showTopBar === true,
          showPhone: tenantConfig?.topBarShowPhone !== false,
          showEmail: tenantConfig?.topBarShowEmail !== false,
          showFacebook: tenantConfig?.topBarShowFacebook === true,
          showX: tenantConfig?.topBarShowX === true,
          showWhatsApp: tenantConfig?.topBarShowWhatsApp === true,
          phone: tenantConfig?.topBarPhone ?? '',
          email: tenantConfig?.topBarEmail ?? '',
          facebookUrl: tenantConfig?.socialFacebook ?? '',
          xUrl: tenantConfig?.socialTwitter ?? '',
          whatsAppUrl: tenantConfig?.socialWhatsApp ?? '',
          backgroundColor: tenantConfig?.topBarBackgroundColor ?? '',
          facebookIconColor: resolveTopBarSocialIconColor(tenantConfig?.topBarSocialFacebookColor),
          xIconColor: resolveTopBarSocialIconColor(tenantConfig?.topBarSocialXColor),
          whatsAppIconColor: resolveTopBarSocialIconColor(tenantConfig?.topBarSocialWhatsAppColor),
          headerFooterTypography,
        };

        const enhanced = enhanceSsrHeader(
          headerMount,
          headerOptions,
          renderWithI18n,
          hydrateWithI18n,
        );

        if (!enhanced) {
          renderWithI18n(<Header {...headerOptions} />, headerMount);
        }
      } catch (err: unknown) {
        console.warn('[pagayo] Failed to render header:', err);
      }
    }

    const footerMount = document.getElementById('footer-mount');
    if (
      footerMount &&
      (!footerMount.querySelector('[data-ssr-footer="true"]') ||
        forceClientFooterRender)
    ) {
      try {
        const shopName = footerMount.dataset.shopName || tenantConfig?.name || 'Webshop';
        const showNewsletter = footerMount.dataset.showNewsletter
          ? footerMount.dataset.showNewsletter !== 'false'
          : tenantConfig?.showFooterNewsletter !== false;
        renderWithI18n(
          <Footer shopName={shopName} showNewsletter={showNewsletter} />,
          footerMount,
        );
      } catch (err: unknown) {
        console.warn('[pagayo] Failed to render footer:', err);
      }
    }
  }).catch((err: unknown) => {
    console.warn('[pagayo] Failed to load layout components:', err);
  });
};

/**
 * Mount non-critical interactive components after FCP.
 * Cart drawer, cart button, user menu — webshop only.
 */
const mountDeferredComponents = () => {
  const pageType = document.body.dataset.pageType ?? "";

  void import('./features/cart/CartDrawer').then(({ CartDrawer }) => {
    const mount = document.getElementById('cart-drawer-mount');
    if (mount) renderWithI18n(<CartDrawer />, mount);
  });

  void import('./features/cart/CartButton').then(({ CartButton }) => {
    const mount = document.getElementById('cart-button-mount');
    if (mount) renderWithI18n(<CartButton />, mount);
  });

  void import('./features/auth/UserMenu').then(({ UserMenu }) => {
    const mount = document.getElementById('user-menu-mount');
    if (mount) renderWithI18n(<UserMenu />, mount);
  });

  // Always mount when the SSR mount point exists. Visibility is owned by
  // AgeGateModal via public settings (enabled + cookie), so enabling the gate
  // mid-session (website editor) still shows without a full page reload.
  if (pageType !== "age-check") {
    const mount = document.getElementById('age-gate-mount');
    if (mount) {
      void import('./components/AgeGateModal').then(({ AgeGateModal }) => {
        renderWithI18n(<AgeGateModal />, mount);
      });
    }
  }
};

// Initialize based on page type
const initPage = async () => {
  const { pageType, isWebshop } = getPageContext();

  // Terminal shells: direct mount (skip webshop chrome + main router)
  if (pageType === "pos") {
    const { initPOS } = await import("./pages/pos");
    initPOS();
    return;
  }
  if (pageType === "pos-check-in") {
    const { initCheckInTerminal } = await import("./pages/check-in-terminal");
    initCheckInTerminal();
    return;
  }
  if (isMembersDeskTerminalPageType(pageType)) {
    const { initMembersBalieTerminal } = await import("./pages/members-balie-terminal");
    initMembersBalieTerminal();
    return;
  }

  // 0. Preload the active webshop locale so the FIRST client paint is already
  //    in the tenant language (prevents EN→NL flash when SSR shells are replaced).
  if (isWebshop) {
    try {
      const { preloadWebshopLocale, detectClientLocale } = await import('./i18n/webshop');
      await preloadWebshopLocale(detectClientLocale());
    } catch (err: unknown) {
      console.warn('[pagayo] Failed to preload webshop locale:', err);
    }
  }

  // 1. Mount critical components FIRST (Header + Footer → FCP, webshop only)
  initStorefrontAppearance(isWebshop);
  mountCriticalComponents(isWebshop);

  // Link interception must not wait for header/footer/i18n hydration.
  if (isWebshop) {
    initWebshopNavigator();
  }

  // 2. Defer non-critical mounts (cart, auth) until after first paint — webshop only
  if (isWebshop) {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(mountDeferredComponents);
    } else {
      setTimeout(mountDeferredComponents, 50);
    }
  }

  // 3. Analytics — lazy loaded, non-blocking (skip for admin + platform)
  if (
    pageType !== "admin" &&
    !pageType.startsWith("pos") &&
    pageType !== "members-desk" &&
    pageType !== "members-balie" &&
    pageType !== "platform" &&
    pageType !== "admin-forgot-password" &&
    pageType !== "admin-reset-password"
  ) {
    void import('./lib/analytics').then((m) => m.initAnalytics());
  }

  switch (pageType) {
    case "home": {
      const { initHome } = await import("./pages/home");
      initHome();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "checkout": {
      const { applyEditorPreviewFixtures } = await import(
        "./features/website-editor/runtime/apply-editor-preview-fixtures"
      );
      applyEditorPreviewFixtures();
      const { initCheckout } = await import("./pages/checkout");
      initCheckout();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "product": {
      const { initProduct } = await import("./pages/product");
      initProduct();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "products": {
      const { initProducts } = await import("./pages/products");
      initProducts();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "brand": {
      const { initBrand } = await import("./pages/brand");
      initBrand();
      break;
    }
    case "cart": {
      const { applyEditorPreviewFixtures } = await import(
        "./features/website-editor/runtime/apply-editor-preview-fixtures"
      );
      applyEditorPreviewFixtures();
      const { initCart } = await import("./pages/cart");
      initCart();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "auth":
    case "login":
    case "register":
    case "create-account":
    case "forgot-password":
    case "reset-password": {
      const { initAuth } = await import("./pages/auth");
      initAuth();
      break;
    }
    case "account":
    case "account-orders":
    case "account-order-detail":
    case "account-subscriptions":
    case "account-subscription-detail":
    case "account-returns":
    case "account-settings":
    case "account-addresses":
    case "account-security": {
      const { applyEditorPreviewFixtures } = await import(
        "./features/website-editor/runtime/apply-editor-preview-fixtures"
      );
      applyEditorPreviewFixtures();
      const { initAccountPage } = await import("./pages/account");
      await initAccountPage();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "search": {
      const { initSearchPage } = await import("./pages/search");
      initSearchPage();
      break;
    }
    case "contact": {
      const { initContactPage } = await import("./pages/contact");
      initContactPage();
      break;
    }
    case "partners": {
      const { initPartnersPage } = await import("./pages/partners");
      initPartnersPage();
      break;
    }
    case "faq": {
      const { initFaqPage } = await import("./pages/faq");
      initFaqPage();
      break;
    }
    case "thank-you": {
      const { initThankYouPage } = await import("./pages/thank-you");
      initThankYouPage();
      break;
    }
    case "checkout-complete": {
      const { initCheckoutComplete } = await import("./pages/checkout-complete");
      initCheckoutComplete();
      break;
    }
    case "age-check": {
      const { initAgeCheck } = await import("./pages/age-check");
      initAgeCheck();
      break;
    }
    case "orders": {
      const { initOrdersPage } = await import("./pages/orders");
      initOrdersPage();
      break;
    }
    case "subscriptions":
    case "subscription-detail": {
      const { initSubscriptionsPage } = await import("./pages/subscriptions");
      initSubscriptionsPage();
      break;
    }
    case "pos": {
      const { initPOS } = await import("./pages/pos");
      initPOS();
      break;
    }
    case "pos-check-in": {
      const { initCheckInTerminal } = await import("./pages/check-in-terminal");
      initCheckInTerminal();
      break;
    }
    case "members-desk":
    case "members-balie": {
      const { initMembersBalieTerminal } = await import("./pages/members-balie-terminal");
      initMembersBalieTerminal();
      break;
    }
    case "check-in-verify": {
      const { initPublicCheckInVerify } = await import("./pages/check-in-verify");
      initPublicCheckInVerify();
      break;
    }
    case "public-pass-share": {
      const { initPublicPassShare } = await import("./pages/public-pass-share");
      initPublicPassShare();
      break;
    }
    case "page": {
      const { initPagePage } = await import("./pages/page");
      initPagePage();
      await mountWebsiteEditorIfBootstrapped();
      break;
    }
    case "blog": {
      const { initBlogListPage } = await import("./pages/blog");
      initBlogListPage();
      break;
    }
    case "blog-post": {
      const { BlogPostPage } = await import("./pages/blog/BlogPostPage");
      const blogPostMount = document.getElementById("blog-post-mount");
      if (blogPostMount) render(<BlogPostPage />, blogPostMount);
      break;
    }
    case "admin": {
      const { initAdmin } = await import("./components/admin/AdminApp");
      initAdmin();
      break;
    }
    case "admin-forgot-password": {
      const { AdminForgotPassword } = await import(
        "./components/admin/AdminForgotPassword"
      );
      const mount = document.getElementById("admin-forgot-mount");
      if (mount) render(<AdminForgotPassword />, mount);
      break;
    }
    case "admin-reset-password": {
      const { AdminResetPassword } = await import(
        "./components/admin/AdminResetPassword"
      );
      const mount = document.getElementById("admin-reset-mount");
      if (mount) render(<AdminResetPassword />, mount);
      break;
    }
    case "set-password": {
      const { SetPasswordPage } = await import(
        "./components/auth/SetPasswordPage"
      );
      const mount = document.getElementById("set-password-mount");
      if (mount) renderWithI18n(<SetPasswordPage />, mount);
      break;
    }
    case "platform": {
      const { initPlatform } = await import("./components/platform/PlatformApp");
      initPlatform();
      break;
    }
    case "upload-photo": {
      const { initUploadPhoto } = await import("./pages/upload-photo");
      initUploadPhoto();
      break;
    }
    default:
      break;
  }
};

// Run on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    registerPwaServiceWorker();
    void initPage();
  });
} else {
  registerPwaServiceWorker();
  void initPage();
}

// Web Vitals — skip kiosk terminal shells (misleading FCP; not webshop SEO targets)
function shouldInitWebVitals(): boolean {
  const pageType = document.body.getAttribute("data-page-type") ?? "";
  return (
    pageType !== "pos" &&
    pageType !== "pos-check-in" &&
    pageType !== "members-desk" &&
    pageType !== "members-balie"
  );
}

if (shouldInitWebVitals()) {
  if (document.readyState === 'complete') {
    void import('./lib/web-vitals').then((m) => m.initWebVitals());
  } else {
    window.addEventListener('load', () => {
      void import('./lib/web-vitals').then((m) => m.initWebVitals());
    });
  }
}

export {};
